import React from 'react';

import Link from 'next/link';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { GROQCTA } from '@/sanity/lib/definitions';
import { useCTAWrapper } from '@/hooks/useCTAWrapper';
import Anchor from '@/components/global/utils/Anchor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shadcn/ui/dialog';
import { customRichTextComponent } from '@/sanity/sanityPortableText';
import HubSpotForm from '@/components/global/partials/HubSpotForm';
import ZapierForm from '@/components/global/partials/ZapierForm';
import Chip from '@/components/global/ui/Chip';
import { cn } from '@/lib/utils';
import DividerGreenLine from '@/components/global/ui/DividerGreenLine';

// import dots from "@/public/launch/dots2.png";
// import accessibility from "@/public/icons/Accessibility--alt.svg";

interface CTAWrapperProps {
  cta: GROQCTA | null | undefined;
  children: React.ReactNode;
  className?: string;
}

export function CTAWrapper({ cta, children, className }: CTAWrapperProps) {
  const { isLink, link, isAnchor, anchor, isPopup, popup } = useCTAWrapper(cta);

  if (isLink && link) {
    return (
      <a href={`${link}`} className={cn('inline-block', className)}>
        {children}
      </a>
    );
  }

  if (isAnchor && anchor) {
    return (
      <Anchor target={anchor} className={cn('inline-block', className)}>
        {children}
      </Anchor>
    );
  }

  if (isPopup && popup) {
    const isNewTheme = popup.theme === 'new';

    return (
      <Dialog>
        <DialogTrigger asChild className="">
          <div className={cn('w-full md:w-auto', className)}>{children}</div>
        </DialogTrigger>
        <DialogContent
          className={cn(
            '!w-[min(88.625rem,calc(100%-4rem))] max-h-[80svh] overflow-y-auto lg:max-h-screen top-[2rem] md:top-1/2 translate-y-0 md:-translate-y-1/2',
            isNewTheme
              ? 'bg-[#112628] border-[3px] border-[#D9F9B9]/10 !rounded-none !p-0'
              : '!bg-white/5 border !border-white/10 !rounded-[2rem] backdrop-blur-[20rem] !p-[.5rem]',
          )}
        >
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle></DialogTitle>
              <DialogDescription></DialogDescription>
            </DialogHeader>
          </VisuallyHidden>

          <div
            className={cn(
              isNewTheme
                ? 'w-full p-[2.5rem] sm:p-[3rem] md:p-[4rem] lg:px-[5rem] lg:py-[6rem] flex flex-col items-start'
                : 'w-full flex flex-col justify-center items-center border !border-white/20 rounded-[2rem] px-[2rem] pt-[4rem] pb-[4rem] lg:pb-[6rem] lg:px-[8rem] relative overflow-clip',
            )}
          >
            {/*{!isNewTheme && (
              <Image
                className="object-cover object-top absolute top-0 left-1/2 w-full h-full opacity-40 -translate-x-1/2 pointer-events-none"
                src={dots}
                alt="background"
              />
            )}*/}

            {/*{isNewTheme && (
              <Chip
                className="prose-img:!w-[3rem] mb-[2rem] lg:mb-[3rem] prose-p:font-medium"
                image={accessibility}
                text="BUCH BESTELLEN"
              />
            )}*/}

            {popup?.content && (
              <div className="flex flex-col">
                <div
                  className={cn(
                    'prose-headings:text-white',
                    isNewTheme
                      ? 'richtext richtext-start prose-headings:!mb-[2rem] prose-headings:font-geist prose-headings:text-[4rem] lg:prose-headings:text-[5rem] prose-headings:font-normal prose-headings:w-[min(65rem,90%)] prose-p:font-geist prose-p:mt-[2rem]'
                      : 'richtext prose-headings:text-[3.75rem] md:prose-headings:text-[6rem] prose-headings:leading-none prose-headings:font-medium prose-p:text-white/70 prose-p:text-[2rem] md:tprose-p:text-[2.25rem] md:prose-p:px-[2rem] prose-p:mt-[2rem] prose-headings:!mb-[2rem]',
                  )}
                >
                  <PortableText
                    value={popup.content}
                    components={customRichTextComponent({})}
                  />
                </div>

                {isNewTheme && (
                  <DividerGreenLine className="w-full my-[2.5rem] lg:my-[4rem]" />
                )}
              </div>
            )}

            {popup?.additionalContent && (
              <div className="richtext richtext-center prose-h2:!mb-[2rem] prose-p:text-white/70 prose-p:font-light prose-strong:font-normal prose-strong:text-white prose-p:font-geist prose-p:leading-[1.2em]">
                <PortableText
                  value={popup.additionalContent}
                  components={customRichTextComponent({})}
                />
              </div>
            )}

            <div className="w-full border-t opacity-20 [border-image:repeating-linear-gradient(to_right,white_0,white_10px,transparent_10px,transparent_20px)_1] my-[2.5rem] lg:my-[4rem]" />

            {popup?.formType === 'hs' && popup?.hsForm && (
              <HubSpotForm formData={popup?.hsForm} theme={popup.theme} />
            )}

            {popup?.formType === 'zapier' && popup?.zapierForm && (
              <ZapierForm formData={popup.zapierForm} theme={popup.theme} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return <div className={cn('inline-block', className)}>{children}</div>;
}
